import React, { useState, useEffect } from "react";
import "../../assets/css/members.css";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import VerticalSpace from "../../Components/VerticalSpace";
import "../../stylesheets/indexpage.css";
import { baseURL, postRequest } from "../../api/apiClient";
import Toast from "../../Components/Common/Toast";
import PaginationComponent from "../../Components/Common/PaginationComponent";
import Loader from "../../utils/Loading";
import gsap from "gsap";
import "../../stylesheets/common.css";
import ShimmerEffect from "../../utils/ShimmerEffect";
import { useDebounce } from "../../hooks/useDebounce";

const InnerRowContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 1100px;
`;

const ScrollableRowContainer = styled(RowContainer)`
  @media (max-width: 1150px) {
    width: 100%;
    overflow-x: auto;
  }
`;

const StyledRowContainer = styled(RowContainer)`
  overflow: hidden;
  max-width: 100%;
  min-width: 0;
  flex-shrink: 1;
`;

const StyledLabel = styled(Label)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-block;
  max-width: 100%;
  min-width: 0;
  width: 100%;
`;

interface EmailSubscriber {
  id: number;
  name: string;
  lastname: string;
  email: string;
}

const EmailSubscribe = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [subscribers, setSubscribers] = useState<EmailSubscriber[]>([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0"));
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit") || "5"));
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const buildParams = (p: number, l: number, s: string): Record<string, string> => {
    const params: Record<string, string> = { page: p.toString(), limit: l.toString() };
    if (s) params.search = s;
    return params;
  };

  const debouncedSearch = useDebounce(searchQuery, 500);

  // Sync state when browser Back/Forward changes the URL
  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") || "0");
    const urlLimit = parseInt(searchParams.get("limit") || "5");
    const urlSearch = searchParams.get("search") || "";
    setPage(urlPage);
    setLimit(urlLimit);
    setSearchQuery(urlSearch);
  }, [searchParams]);

  const fetchSubscribers = async (page: number, perPage: number, search?: string) => {
    setLoading(true);
    try {
      const url = `${baseURL}/email-subscribe/get-all`;

      const body = {
        page,
        limit: perPage,
        search: search || "",
      };

      const response = await postRequest(url, body);

      if (response?.data) {
        setSubscribers(response.data.data.subscribers || []);
        setCount(response.data.data.totalCount || 0);
        setPage(response.data.data.page - 1);
        setHasNextPage(response.data.data.hasNextPage || false);
        setHasPreviousPage(response.data.data.hasPreviousPage || false);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching email subscribers:", error);
    }
  };

  useEffect(() => {
    fetchSubscribers(page + 1, limit, debouncedSearch);
  }, [debouncedSearch, page, limit]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(0);
    setSearchParams(buildParams(0, limit, query));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    setSearchParams(buildParams(newPage, limit, searchQuery));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    setSearchParams(buildParams(0, newLimit, searchQuery));
  };

  const handleExportCSV = async () => {
    try {
      const body = {
        search: debouncedSearch,
      };
      const response = await postRequest(`${baseURL}/email-subscribe/export-csv`, body);
      if (response?.data?.success) {
        const csvData = response.data.data.csv;
        const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Email_Subscribers_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        Toast("CSV exported successfully", "success");
      } else {
        Toast("Failed to export CSV", "error");
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      Toast("Failed to export CSV", "error");
    }
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.2,
    });
  }, [loading]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="container-fluid">
            <div className="p-3">
              <div className="row mb-3">
                <div className="col">
                  <h2 style={{ fontWeight: "bold" }}>Email Subscribers</h2>
                </div>
              </div>

              {/* Search Bar Row */}
              <div className="row mb-3 all-button-responsive">
                <div className="col-3 search-input-div">
                  <input
                    type="search"
                    className="form-control rounded"
                    placeholder="Search by email"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    aria-label="Search"
                    aria-describedby="search-addon"
                    style={{
                      height: "100%",
                      boxShadow: "none",
                    }}
                  />
                </div>
                <div className="col d-flex justify-content-end responsive">
                  <button
                    type="button"
                    className="btn btn-success user"
                    style={{
                      backgroundColor: "#FF9E00",
                      borderRadius: "10px",
                      color: "#100F15",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={handleExportCSV}
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              <ScrollableRowContainer>
                <InnerRowContainer>
                  <Container width="100%">
                    <RowContainer
                      alignItem="center"
                      padding="8px 12px 8px 12px"
                      gap="8px"
                      justifyContent="space-between"
                      className="row-container"
                    >
                      <RowContainer flex="1">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          #
                        </Label>
                      </RowContainer>
                      <RowContainer flex="4">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Name
                        </Label>
                      </RowContainer>
                      <RowContainer flex="4">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Lastname
                        </Label>
                      </RowContainer>
                      <RowContainer flex="6">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Email
                        </Label>
                      </RowContainer>
                    </RowContainer>

                    {loading ? (
                      <>
                        <RowContainer
                          borderRadius="8px"
                          bgColor="#ffffff"
                          padding="10px 0px"
                        >
                          {Array.from({ length: 4 }, (_, index) => {
                            return (
                              <RowContainer
                                key={index}
                                width={
                                  index === 0
                                    ? "10%"
                                    : index === 1 || index === 2
                                      ? "25%"
                                      : "40%"
                                }
                                justifyContent="center"
                              >
                                <ShimmerEffect />
                              </RowContainer>
                            );
                          })}
                        </RowContainer>
                      </>
                    ) : (
                      <>
                        {subscribers && subscribers.length > 0 ? (
                          <ColumnContainer>
                            {subscribers.map((subscriber, index) => (
                              <Container
                                key={subscriber.id}
                                bgColor="#fff"
                                borderRadius="10px"
                                height="50px"
                                alignItems="center"
                                display="flex"
                                width="100%"
                                className="user-data user-container-hover"
                              >
                                <RowContainer
                                  justifyContent="space-between"
                                  alignItem="center"
                                  borderRadius="12px"
                                  margin="10px"
                                >
                                  <StyledRowContainer flex="1" alignItem="center">
                                    <StyledLabel fontSize="16px" fontWeight={400}>
                                      {page * limit + index + 1}
                                    </StyledLabel>
                                  </StyledRowContainer>
                                  <StyledRowContainer flex="4">
                                    <StyledLabel fontSize="16px" fontWeight={400}>
                                      {subscriber.name || "-"}
                                    </StyledLabel>
                                  </StyledRowContainer>
                                  <StyledRowContainer flex="4">
                                    <StyledLabel fontSize="16px" fontWeight={400}>
                                      {subscriber.lastname || "-"}
                                    </StyledLabel>
                                  </StyledRowContainer>
                                  <StyledRowContainer flex="6">
                                    <StyledLabel fontSize="16px" fontWeight={400}>
                                      {subscriber.email}
                                    </StyledLabel>
                                  </StyledRowContainer>
                                </RowContainer>
                              </Container>
                            ))}
                          </ColumnContainer>
                        ) : (
                          <>
                            <VerticalSpace height={30} />
                            <Label
                              alignItems="center"
                              display="flex"
                              color="rgba(164, 163, 167, 1)"
                              justifyContent="center"
                            >
                              No email subscribers found.
                            </Label>
                          </>
                        )}
                      </>
                    )}
                  </Container>
                </InnerRowContainer>
              </ScrollableRowContainer>
              <VerticalSpace height={10} />
              <RowContainer justifyContent="flex-end" className="user-data">
                <PaginationComponent
                  page={page}
                  rowsPerPage={limit}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={count}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                />
              </RowContainer>
            </div>
            <VerticalSpace height={100} />
          </div>
        </>
      )}
    </>
  );
};

export default EmailSubscribe;
